import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { supabase } from '../../config/supabase';
import { SupabaseEventRow } from '../../types/supabase';
import { useAuth } from '../../context/AuthContext';

interface EventForm {
  eventTitle: string;
  eventDate: string;
  eventTime: string;
  eventLocation: string;
  eventAddress: string;
  eventDescription: string;
  eventCapacity: number;
  eventImage: string;
  eventRegistrationLink: string;
}

interface EventEditorProps {
  onClose: () => void;
}

const EventEditor = ({ onClose }: EventEditorProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { user, selectedClient } = useAuth();
  const { register, handleSubmit, formState: { errors } } = useForm<EventForm>();

  const onSubmit = async (data: EventForm) => {
    const activeClientId = selectedClient?.clientId || user?.current_client_id || null;

    if (!user?.userId) {
      toast.error("Você precisa estar autenticado para criar eventos");
      return;
    }

    if (!activeClientId) {
      toast.error("Você precisa selecionar um cliente para criar eventos");
      return;
    }

    setIsLoading(true);
    try {
      const eventDateTime = new Date(`${data.eventDate}T${data.eventTime}`);

      const eventData: Omit<SupabaseEventRow, "eventId"> = {
        clientId: activeClientId,
        userId: user.userId,
        eventTitle: data.eventTitle.trim(),
        eventDate: eventDateTime.toISOString(),
        eventLocation: data.eventLocation.trim(),
        eventAddress: data.eventAddress.trim(),
        eventDescription: data.eventDescription.trim(),
        eventCapacity: data.eventCapacity,
        eventImage: data.eventImage.trim(),
        eventRegistrationLink: data.eventRegistrationLink.trim(),
        eventCreatedAt: new Date().toISOString(),
        eventUpdatedAt: new Date().toISOString(),
        eventStatus: "active"
      };

      const { error } = await supabase
        .from("events")
        .insert(eventData);
      
      if (error) throw error;
      toast.success("Evento criado com sucesso!");
      onClose();
    } catch (error) {
      console.error("Error creating event:", error);
      toast.error("Erro ao criar evento");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-2xl font-semibold mb-6">Criar Novo Evento</h2>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700">Título</label>
          <input
            type="text"
            {...register("eventTitle", { required: "Título é obrigatório" })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
          {errors.eventTitle && (
            <p className="mt-1 text-sm text-red-600">{errors.eventTitle.message}</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Data</label>
            <input
              type="date"
              {...register("eventDate", { required: "Data é obrigatória" })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
            {errors.eventDate && (
              <p className="mt-1 text-sm text-red-600">{errors.eventDate.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Horário</label>
            <input
              type="time"
              {...register("eventTime", { required: "Horário é obrigatório" })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
            {errors.eventTime && (
              <p className="mt-1 text-sm text-red-600">{errors.eventTime.message}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Local</label>
          <input
            type="text"
            {...register("eventLocation", { required: "Local é obrigatório" })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
          {errors.eventLocation && (
            <p className="mt-1 text-sm text-red-600">{errors.eventLocation.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Endereço</label>
          <input
            type="text"
            {...register("eventAddress", { required: "Endereço é obrigatório" })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
          {errors.eventAddress && (
            <p className="mt-1 text-sm text-red-600">{errors.eventAddress.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Descrição</label>
          <textarea
            {...register("eventDescription", { required: "Descrição é obrigatória" })}
            rows={4}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
          {errors.eventDescription && (
            <p className="mt-1 text-sm text-red-600">{errors.eventDescription.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Capacidade</label>
          <input
            type="number"
            {...register("eventCapacity", {
              required: "Capacidade é obrigatória",
              min: { value: 1, message: "Capacidade deve ser maior que 0" }
            })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
          {errors.eventCapacity && (
            <p className="mt-1 text-sm text-red-600">{errors.eventCapacity.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">URL da Imagem</label>
          <input
            type="url"
            {...register("eventImage", { required: "URL da imagem é obrigatória" })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            placeholder="https://exemplo.com/imagem.jpg"
          />
          {errors.eventImage && (
            <p className="mt-1 text-sm text-red-600">{errors.eventImage.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Link de Inscrição</label>
          <input
            type="url"
            {...register("eventRegistrationLink", { required: "Link de inscrição é obrigatório" })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            placeholder="https://exemplo.com/inscricao"
          />
          {errors.eventRegistrationLink && (
            <p className="mt-1 text-sm text-red-600">{errors.eventRegistrationLink.message}</p>
          )}
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {isLoading ? 'Criando...' : 'Criar Evento'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EventEditor;
